import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { paymentService } from '../../services/paymentService';
import { companyService } from '../../services/companyService';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters';
import { Payment } from '../../types/payment';
import { TransactionReceipt } from './TransactionReceipt';

const statusStyle: Record<string, React.CSSProperties> = {
  COMPLETED: { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  PENDING:   { background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' },
  FAILED:    { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' },
  CANCELLED: { background: 'rgba(80,80,80,0.15)', color: '#555570', border: '1px solid rgba(80,80,80,0.2)' },
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const PaymentsList: React.FC = () => {
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: paymentsData, isLoading, error } = useQuery({
    queryKey: ['payments', currentPage, searchTerm],
    queryFn: () => paymentService.getPayments({
      page: currentPage,
      limit: pageSize,
      search: searchTerm || undefined
    })
  });

  const { data: companyProfile } = useQuery({
    queryKey: ['company', 'profile'],
    queryFn: companyService.getProfile
  });

  const companyAddress = [
    companyProfile?.address,
    companyProfile?.city,
    companyProfile?.state,
    companyProfile?.postalCode,
    companyProfile?.country
  ].filter(Boolean).join(' ');

  const handleViewReceipt = (payment: Payment) => {
    setSelectedPayment(payment);
    setShowReceipt(true);
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedPayment(null);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('transaction-receipt');
    const originalContent = document.body.innerHTML;
    if (printContent) {
      document.body.innerHTML = printContent.outerHTML;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const payments = paymentsData?.payments || [];
  const pagination = paymentsData?.pagination;

  if (showReceipt && selectedPayment) {
    return (
      <TransactionReceipt
        payment={selectedPayment}
        companyName={companyProfile?.name || 'Company Name'}
        companyAddress={companyAddress || 'Company Address'}
        companyPhone={companyProfile?.phone || ''}
        companyEmail={companyProfile?.email || ''}
        companyLogo={companyProfile?.logo || undefined}
        onClose={handleCloseReceipt}
        onPrint={handlePrint}
      />
    );
  }

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>Loading payments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Error loading payments. Please try again.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Payment History</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>View and print receipts for all payments</div>
        </div>
        <div style={{ position: 'relative' }}>
          <svg width="14" height="14" fill="none" stroke="rgba(217,119,6,0.35)" viewBox="0 0 24 24" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search payments..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              paddingLeft: 32,
              paddingRight: 12,
              paddingTop: 8,
              paddingBottom: 8,
              background: '#f4efe2',
              border: '1px solid rgba(92,78,51,0.25)',
              borderRadius: 4,
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: '#5b5a52',
              outline: 'none',
              width: 220,
            }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(217,119,6,0.35)'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(92,78,51,0.25)'; }}
          />
        </div>
      </div>

      {/* Table */}
      {payments.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                <th style={thStyle}>Transaction #</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Customer / Vendor</th>
                <th style={thStyle}>Invoice / Bill</th>
                <th style={thStyle}>Method</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr
                  key={payment.id}
                  style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={tdStyle}>
                    <div style={{ color: '#d97706' }}>{payment.transactionNumber}</div>
                    {payment.reference && (
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>Ref: {payment.reference}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <div>{formatDate(payment.paymentDate)}</div>
                    <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{formatDateTime(payment.createdAt)}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{payment.customer?.name || payment.vendor?.name || 'N/A'}</div>
                    {(payment.customer?.email || payment.vendor?.email) && (
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                        {payment.customer?.email || payment.vendor?.email}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {payment.invoice?.invoiceNumber || payment.bill?.billNumber || 'General Payment'}
                  </td>
                  <td style={tdStyle}>
                    {paymentService.formatPaymentMethod(payment.paymentMethod)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                      {formatCurrency(payment.amount)}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      ...(statusStyle[payment.status] || statusStyle.COMPLETED),
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 3,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                    }}>
                      {payment.status}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={() => handleViewReceipt(payment)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 12px',
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 8,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: '#d97706',
                        background: 'rgba(239,232,213,0.6)',
                        border: '1px solid rgba(92,78,51,0.3)',
                        borderRadius: 3,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(92,78,51,0.22)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,232,213,0.6)'; }}
                      title="View and print receipt"
                    >
                      <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Receipt
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>
            {searchTerm ? 'NO RESULTS FOUND' : 'NO PAYMENTS YET'}
          </p>
          {searchTerm && (
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 6 }}>
              Try adjusting your search terms.
            </p>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}–{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} payments
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: currentPage <= 1 ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ padding: '4px 12px', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
              disabled={currentPage >= pagination.totalPages}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: currentPage >= pagination.totalPages ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: currentPage >= pagination.totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
